'use client'

import ProductCard from "../product/ProductCard"
import { CircleFadingPlus, ArrowDownWideNarrow, ArrowUp, Filter, Medal, MinusIcon, PackageOpen, Percent, PlusIcon, Sparkles, Clock, Loader2 } from "lucide-react"
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuItem, MenuItems, Transition, TransitionChild } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import { classNames, formatImageURL, getAspectRatioClass, getBackgroundColorStyles, getFocusRingColorStyles, getTextColorStyles, getVendorPrimaryColor, getProductTags, hasLoyaltyTierBenefits, isLoyaltyActive } from "@/utils/util"
import { SORT_OPTIONS, SPECIAL_CATEGORY_LABELS, SPECIAL_CATEGORY_FILTERS, findCategoryById, getCategoryDescendantIds, buildInitialProductFilters, type SpecialCategoryFilter } from "@/utils/product"
import { useScrollVisibility } from "@/hooks/use-scroll-visibility"
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll"
import Skeleton from "@/components/elements/Skeleton"
import { Button } from "@/components/elements/Button"
import Select from "@/components/elements/Select"
import EmptyState from "@/components/elements/EmptyState"
import { useInfiniteProducts } from "@/hooks/use-infinite-products"
import { useCart } from "@/lib/commerce/hooks"
import { Session } from "@/utils/types"

const secondNav = [
  { id: 'featured', name: 'Онцлох', icon: Sparkles },
  { id: 'new', name: 'Шинэ', icon: CircleFadingPlus },
  { id: 'sale', name: 'Хямдралтай', icon: Percent },
  { id: 'preorder', name: 'Захиалгат', icon: Clock },
  { id: 'loyalty', name: 'Лоялти', icon: Medal },
]

export default function ProductList({ vendor, categoryList, brandList, session }: { vendor: any, categoryList: any, brandList: any, wishlist?: any, session?: Session | null }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const categories = categoryList || []
  const brands = brandList || []
  const [tags, setTags] = useState<string[]>([])
  const [localFilters, setLocalFilters] = useState<{ type: string, value: string }[]>([])
  const [sort, setSort] = useState('latest')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [isCategoryLoading, setIsCategoryLoading] = useState(false)
  const isNavVisible = useScrollVisibility()

  const getInitialFilters = useCallback(() => buildInitialProductFilters(searchParams, categoryList), [searchParams, categoryList])

  const { loyaltySummary } = useCart()
  const navItems = useMemo(() => secondNav.filter((item) => item.id !== 'loyalty' || (isLoyaltyActive(vendor) && hasLoyaltyTierBenefits(loyaltySummary))), [vendor, loyaltySummary])

  const { products, isLoading, isLoadingMore, metadata, loadMore, updateFilters, hasMore, pendingCount } = useInfiniteProducts({
    vendorId: vendor?.id,
    limit: 20,
    initialFilters: getInitialFilters(),
    session
  })

  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: loadMore,
    enabled: hasMore && !isLoading && !isLoadingMore,
  })

  useEffect(() => {
    if (products && products.length > 0) {
      const allTags = products.flatMap(p => getProductTags(p))
      const uniqueTags = [...new Set(allTags)].filter(t => t !== '').sort((a, b) => a.localeCompare(b))
      setTags(uniqueTags)
    }
  }, [products])

  const filterList = useMemo(() => ([
    { id: 'brand', name: 'Брэнд', options: (brands || []).map((b: any) => ({ value: b.id, label: b.name, checked: false })) },
    { id: 'tags', name: 'Шошго', options: tags.map(t => ({ value: t, label: t, checked: false })) },
  ]), [brands, tags])

  useEffect(() => {
    const newFilters: { type: string, value: string }[] = []

    if (searchParams.has('brand')) {
      const brandParams = searchParams.get('brand')?.split(',') || []
      brandParams.forEach(brandId => {
        newFilters.push({ type: 'brand', value: brandId })
      })
    }

    if (searchParams.has('tag')) {
      const tagParams = searchParams.get('tag')?.split(',') || []
      tagParams.forEach(tag => {
        newFilters.push({ type: 'tags', value: tag })
      })
    }
    if (newFilters.length > 0) {
      setLocalFilters(newFilters)
    }
  }, [searchParams])

  const currentCategory = useMemo(() => {
    const categoryParam = searchParams.get('category')
    const brandParam = searchParams.get('brand')
    const tagParam = searchParams.get('tag')

    if (brandParam || tagParam) {
      const selectedItems = []
      let brandThumbnail = null

      if (brandParam) {
        const brandIds = brandParam.split(',')
        if (brands && brands.length > 0) {
          const selectedBrands = brands.filter((brand: any) => brandIds.includes(brand.id))
          if (selectedBrands.length === 1) {
            selectedItems.push(selectedBrands[0].name)
            brandThumbnail = selectedBrands[0].thumbnail
          } else if (selectedBrands.length > 1) {
            selectedItems.push(`${selectedBrands.length} брэнд`)
          }
        } else {
          selectedItems.push('Брэнд')
        }
      }

      if (tagParam) {
        const selectedTags = tagParam.split(',')
        if (selectedTags.length === 1) {
          selectedItems.push(selectedTags[0])
        } else if (selectedTags.length > 1) {
          selectedItems.push(`${selectedTags.length} шошго`)
        }
      }

      if (selectedItems.length > 0) {
        return {
          name: selectedItems.join(' • '),
          id: 'filtered',
          thumbnail: brandThumbnail,
        }
      }
    }

    if (!categoryParam) return null

    if (SPECIAL_CATEGORY_FILTERS.includes(categoryParam as SpecialCategoryFilter)) {
      return { name: SPECIAL_CATEGORY_LABELS[categoryParam as SpecialCategoryFilter], id: categoryParam }
    }

    return findCategoryById(categoryList, categoryParam)
  }, [searchParams, categoryList, brands])

  const sortHandler = (sortName: string) => {
    setSort(sortName)
    updateFilters({ sort: sortName })
  }

  const filterHandler = (type: string, e: any) => {
    const value = e.target.value
    const checked = e.target.checked
    let newFilters: { type: string, value: string }[]

    if (checked) {
      newFilters = [...localFilters, { type, value }]
    } else {
      newFilters = localFilters.filter(f => !(f.type === type && f.value === value))
    }
    setLocalFilters(newFilters)
    updateFiltersInURL(newFilters)
  }

  const updateFiltersInURL = (currentFilters: { type: string, value: string }[]) => {
    const params = new URLSearchParams(searchParams.toString())

    const brandFilters = currentFilters
      .filter(f => f.type === 'brand')
      .map(f => f.value)

    const tagFilters = currentFilters
      .filter(f => f.type === 'tags')
      .map(f => f.value)

    if (brandFilters.length > 0) {
      params.set('brand', brandFilters.join(','))
    } else {
      params.delete('brand')
    }

    if (tagFilters.length > 0) {
      params.set('tag', tagFilters.join(','))
    } else {
      params.delete('tag')
    }

    updateFilters({
      brand: brandFilters.length > 0 ? brandFilters.join(',') : null,
      tag: tagFilters.length > 0 ? tagFilters.join(',') : null
    })

    router.push(`${pathname}?${params.toString()}`, { scroll: false })
  }

  const checkFilterCheckbox = (type: string, value: string): boolean => {
    return localFilters.some(f => f.type === type && f.value === value)
  }

  const createQueryString = useCallback((name: string, value: any) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set(name, value)
    return params.toString()
  }, [searchParams])

  const setCategoryParams = (id: any) => {
    if (id == null) {
      updateFilters({ category: null, filter: null })
      return router.push(pathname)
    }

    if (SPECIAL_CATEGORY_FILTERS.includes(id as SpecialCategoryFilter)) {
      updateFilters({ category: null, filter: id })
    } else {
      const foundCategory = findCategoryById(categoryList, id)
      const categoryIds = foundCategory ? getCategoryDescendantIds(foundCategory) : [id]
      updateFilters({ category: categoryIds.join(','), filter: null })
    }

    router.push(pathname + '?' + createQueryString('category', id))
  }

  const aspectRatioClass = getAspectRatioClass(vendor?.imageAspectRatio)
  const primaryColor = getVendorPrimaryColor(vendor)

  const { className: backgroundColorClass, style: backgroundColorStyle } = getBackgroundColorStyles(primaryColor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)
  const { className: focusRingColorClass, style: focusRingColorStyle } = getFocusRingColorStyles(primaryColor)

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="cont py-8">
      <div className="sticky top-15 sm:top-21 z-30 lg:relative lg:top-auto lg:z-auto bg-background/75 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none rounded-lg">
        <div className="flex flex-col lg:flex-row items-baseline justify-between border-b border-muted-foreground/10 py-2 px-3 lg:px-0">
          <div className="flex flex-row lg:flex-col justify-between items-center lg:items-start gap-2 w-full">
            <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
              <div className="flex items-center gap-3">
                <span className="line-clamp-1">{currentCategory ? currentCategory.name : 'Бараа бүтээгдэхүүн'}</span>
                {currentCategory?.thumbnail && (
                  <div className="w-8 h-8 rounded-md overflow-hidden shrink-0">
                    <img src={formatImageURL(currentCategory.thumbnail)} alt={currentCategory.name} className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
            <span className="block lg:hidden text-sm text-muted-foreground text-center min-w-[50px]">
              {isLoading ? (
                <Skeleton className="h-5 w-16" />
              ) : (
                metadata.total + ' бүт.'
              )}
            </span>
            <span className="hidden lg:block text-sm text-muted-foreground">
              {isLoading ? (
                <Skeleton className="h-5 w-36" />
              ) : (
                metadata.total + ' бүтээгдэхүүн'
              )}
            </span>
          </div>
          <div className="flex w-full lg:w-fit justify-between lg:items-center gap-2.5 mt-2 lg:mt-0">
            <div className="lg:hidden">
              <button type="button" onClick={() => setMobileFiltersOpen(true)} className={`flex w-full items-center justify-center gap-x-2 rounded-md ${backgroundColorClass} px-3 py-2 text-xs font-medium text-white shadow-sm w-fit min-w-[120px] sm:min-w-[170px] sm:max-w-[170px]`} style={backgroundColorStyle}>
                <Filter className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                Шүүлт
              </button>
            </div>
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Select value={sort} onChange={(v) => sortHandler(v)} items={SORT_OPTIONS} icon={ArrowDownWideNarrow} containerClass="!w-fit" className="h-9!" />
              </div>
              <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95" >
                <MenuItems className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-zinc-200 focus:outline-none">
                  <div className="py-1">
                    {SORT_OPTIONS.map((option: any) => (
                      <MenuItem key={option.name}>
                        {({ active }) => (
                          <span onClick={() => sortHandler(option.id)} className={classNames(option.current ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground', active ? 'bg-muted text-foreground' : '', 'block px-4 py-2 text-sm cursor-pointer')}>
                            {option.name}
                          </span>
                        )}
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>

      <Transition show={mobileFiltersOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setMobileFiltersOpen}>
          <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0" >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 z-40 flex justify-end">
            <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="translate-x-full" >
              <DialogPanel className="relative flex w-[82%] max-w-xs flex-col overflow-hidden bg-white shadow-2xl rounded-l-2xl">
                <div className="sticky top-0 bg-white/95 backdrop-blur z-10 flex justify-between items-center px-4 h-14 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Filter className={`h-4 w-4 ${textColorClass}`} style={textColorStyle} strokeWidth={2.5} />
                    <h2 className="text-base font-semibold text-foreground">Шүүлт</h2>
                  </div>
                  <button type="button" className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileFiltersOpen(false)}>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form className="flex-1 overflow-y-auto overscroll-contain">
                  <div className="px-3 py-3">
                    <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase px-3 mb-1.5">Бүлэг</div>
                    <div className="flex flex-col gap-1">
                      {navItems.map((item) => (
                        <span key={item.name} onClick={() => setCategoryParams(item.id)} className={classNames((searchParams.has('category') && (searchParams.get('category') == item.id)) ? `bg-muted ${textColorClass}` : `text-foreground hover:bg-muted`, 'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer')} style={(searchParams.has('category') && (searchParams.get('category') == item.id)) ? textColorStyle : undefined}>
                          <item.icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                          <span className="flex-1">{item.name}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="px-3 pb-3 border-t border-border pt-3">
                    <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase px-3 mb-1.5">Ангилал</div>
                    {isCategoryLoading ? (
                      <Skeleton className="h-8 w-1/2 mx-3" />
                    ) : searchParams && (
                      <div className="flex flex-col gap-0.5">
                        <span onClick={() => setCategoryParams(null)} className={classNames(!searchParams.has('category') ? `bg-muted ${textColorClass}` : 'text-foreground hover:bg-muted', 'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer')} style={!searchParams.has('category') ? textColorStyle : undefined}>
                          Бүгд
                        </span>
                        {categories.map((category: any) => {
                          const isCategoryActive = searchParams.has('category') && (searchParams.get('category') === category.slug || searchParams.get('category') === category.id)
                          const isCategoryExpanded = searchParams.has('category') && (searchParams.get('category') === (category.slug || category.id) || category.children?.some((child: any) => searchParams.get('category') === (child.slug || child.id) || child.children?.some((grandChild: any) => searchParams.get('category') === (grandChild.slug || grandChild.id))))
                          return (
                            <div key={category.id}>
                              <span onClick={() => setCategoryParams(category.slug || category.id)} className={classNames(isCategoryActive ? `bg-muted ${textColorClass}` : 'text-foreground hover:bg-muted', 'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors cursor-pointer')} style={isCategoryActive ? textColorStyle : undefined}>
                                {category.name}
                              </span>
                              {isCategoryExpanded && category.children && category.children.length > 0 && (
                                <div className="ml-3 border-l border-border pl-2 mt-0.5">
                                  {category.children.map((child: any) => {
                                    const isChildActive = searchParams.has('category') && (searchParams.get('category') === child.slug || searchParams.get('category') === child.id)
                                    const isChildExpanded = searchParams.has('category') && (searchParams.get('category') === (child.slug || child.id) || child.children?.some((grandChild: any) => searchParams.get('category') === (grandChild.slug || grandChild.id)))
                                    return (
                                      <div key={child.id}>
                                        <span onClick={() => setCategoryParams(child.slug || child.id)} className={classNames(isChildActive ? `bg-muted ${textColorClass}` : 'text-muted-foreground hover:bg-muted hover:text-foreground', 'flex items-center rounded-lg px-3 py-1.5 text-sm transition-colors cursor-pointer')} style={isChildActive ? textColorStyle : undefined}>
                                          {child.name}
                                        </span>
                                        {isChildExpanded && child.children && child.children.length > 0 && (
                                          <div className="ml-3 border-l border-border pl-2 mt-0.5">
                                            {child.children.map((grandChild: any) => {
                                              const isGrandActive = searchParams.has('category') && (searchParams.get('category') === grandChild.slug || searchParams.get('category') === grandChild.id)
                                              return (
                                                <span key={grandChild.id} onClick={() => setCategoryParams(grandChild.slug || grandChild.id)} className={classNames(isGrandActive ? `bg-muted ${textColorClass}` : 'text-muted-foreground hover:bg-muted hover:text-foreground', 'flex items-center rounded-lg px-3 py-1.5 text-sm transition-colors cursor-pointer')} style={isGrandActive ? textColorStyle : undefined}>
                                                  {grandChild.name}
                                                </span>
                                              )
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {!isCategoryLoading && (
                    <div className="px-3 pb-6 border-t border-border pt-3">
                      {filterList.filter(s => s.id !== 'brand' || vendor?.modules?.includes('brand')).map((section) => (
                        <Disclosure as="div" key={section.id}>
                          {({ open }) => (
                            <>
                              <DisclosureButton className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors w-full">
                                <span className="flex-1 text-left">{section.name}</span>
                                {open ? (
                                  <MinusIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                ) : (
                                  <PlusIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                                )}
                              </DisclosureButton>
                              <DisclosurePanel as="div" className="ml-3 border-l border-border pl-2 mt-0.5 mb-2">
                                <div className="flex flex-col gap-1 px-3 py-2">
                                  {section.options.map((option: any, optionIdx: number) => (
                                    <div key={option.value} className="flex items-center">
                                      <input onClick={(e) => filterHandler(section.id, e as any)} id={`filter-mobile-${section.id}-${optionIdx}`} name={`${section.id}[]`} defaultValue={option.value} type="checkbox" defaultChecked={checkFilterCheckbox(section.id, option.value)} className={`h-4 w-4 rounded border-muted-foreground ${textColorClass} ${focusRingColorClass}`} style={{ ...textColorStyle, ...focusRingColorStyle }}/>
                                      <label htmlFor={`filter-mobile-${section.id}-${optionIdx}`} className="ml-3 min-w-0 flex-1 text-sm text-muted-foreground">
                                        {option.label}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </DisclosurePanel>
                            </>
                          )}
                        </Disclosure>
                      ))}
                    </div>
                  )}
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      <div className="pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-x-4 gap-y-10">
          <form className="hidden lg:block lg:sticky lg:top-40 lg:self-start lg:max-h-[calc(100vh-9.25rem)] lg:overflow-y-auto lg:overscroll-contain lg:pr-2 lg:pb-6">
            <nav className="flex flex-1 flex-col" aria-label="Sidebar">
              <ul role="list" className="-mx-2">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <span onClick={() => setCategoryParams(item.id)} className={classNames((searchParams.has('category') && (searchParams.get('category') == item.id)) ? `bg-muted ${textColorClass}` : `text-foreground hover:text-foreground/80 hover:bg-muted`, 'group flex gap-x-2 rounded-md p-2 text-sm leading-6 font-semibold items-center cursor-pointer')} style={(searchParams.has('category') && (searchParams.get('category') == item.id)) ? textColorStyle : undefined}>
                      <item.icon className={classNames((searchParams.has('category') && (searchParams.get('category') == item.id)) ? `${textColorClass}` : `text-muted-foreground group-hover:text-foreground`, 'h-5 w-5 shrink-0')} style={(searchParams.has('category') && (searchParams.get('category') == item.id)) ? textColorStyle : undefined} />
                      <span>{item.name}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </nav>
            <ul role="list" className="mt-3 space-y-3 border-b border-foreground/10 pb-6 text-sm font-medium text-foreground">
              {isCategoryLoading ? (
                <Skeleton className="h-8 w-1/2" />
              ) : (
                <>
                  {searchParams && (
                    <>
                      <li key='all' className="cursor-pointer">
                        <span onClick={() => setCategoryParams(null)} className={classNames(!searchParams.has('category') ? `${textColorClass}` : '', 'block py-1')} style={!searchParams.has('category') ? textColorStyle : undefined}>
                          Бүгд
                        </span>
                      </li>
                      {categories.map((category: any) => (
                        <li key={category.id} className="cursor-pointer">
                          <span onClick={() => setCategoryParams(category.slug || category.id)} className={classNames((searchParams.has('category') && (searchParams.get('category') === category.slug || searchParams.get('category') === category.id)) ? 'text-foreground' : '', 'block py-1')} style={(searchParams.has('category') && (searchParams.get('category') === category.slug || searchParams.get('category') === category.id)) ? textColorStyle : undefined}>
                            {category.name}
                          </span>
                          {/* Level 1 children */}
                          {(searchParams.has('category') && (searchParams.get('category') === (category.slug || category.id) || category.children?.some((child: any) => searchParams.get('category') === (child.slug || child.id) || child.children?.some((grandChild: any) => searchParams.get('category') === (grandChild.slug || grandChild.id))))) && category.children && category.children.length > 0 && (
                            <ul role="list" className="mt-2 space-y-2 text-sm font-medium text-muted-foreground">
                              {category.children.map((child: any) => (
                                <li key={child.id} className="cursor-pointer ml-4">
                                  <span onClick={() => setCategoryParams(child.slug || child.id)} className={classNames((searchParams.has('category') && (searchParams.get('category') === child.slug || searchParams.get('category') === child.id)) ? `text-foreground` : 'text-muted-foreground hover:text-foreground', 'block py-1')} style={(searchParams.has('category') && (searchParams.get('category') === child.slug || searchParams.get('category') === child.id)) ? textColorStyle : undefined}>
                                    {child.name}
                                  </span>
                                  {/* Level 2 children */}
                                  {(searchParams.has('category') && (searchParams.get('category') === (child.slug || child.id) || child.children?.some((grandChild: any) => searchParams.get('category') === (grandChild.slug || grandChild.id)))) && child.children && child.children.length > 0 && (
                                    <ul role="list" className="mt-2 space-y-2 text-sm font-medium text-muted-foreground/70">
                                      {child.children.map((grandChild: any) => (
                                        <li key={grandChild.id} className="cursor-pointer ml-4">
                                          <span onClick={() => setCategoryParams(grandChild.slug || grandChild.id)} className={classNames((searchParams.has('category') && (searchParams.get('category') === grandChild.slug || searchParams.get('category') === grandChild.id)) ? `text-foreground` : 'text-muted-foreground hover:text-foreground', 'block py-1')} style={(searchParams.has('category') && (searchParams.get('category') === grandChild.slug || searchParams.get('category') === grandChild.id)) ? textColorStyle : undefined}>
                                            {grandChild.name}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </>
                  )}
                </>
              )}
            </ul>

            {!isCategoryLoading && (
              <>
                {filterList.map((section) => (
                  <Disclosure as="div" key={section.id} className="border-b border-foreground/10 py-6">
                    {({ open }) => (
                      <>
                        <h3 className="-my-3 flow-root">
                          <DisclosureButton className="flex w-full items-center justify-between bg-white py-3 text-sm text-muted-foreground hover:text-foreground">
                            <span className="font-medium text-foreground">{section.name}</span>
                            <span className="ml-6 flex items-center">
                              {open ? (
                                <MinusIcon className="h-5 w-5" aria-hidden="true" />
                              ) : (
                                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                              )}
                            </span>
                          </DisclosureButton>
                        </h3>
                        <DisclosurePanel className="pt-6">
                          <div className="space-y-4">
                            {section.options.map((option: any, optionIdx: number) => (
                              <div key={option.value} className="flex items-center">
                                <input onClick={(e) => filterHandler(section.id, e as any)} id={`filter-${section.id}-${optionIdx}`} name={`${section.id}[]`} defaultValue={option.value} type="checkbox" defaultChecked={checkFilterCheckbox(section.id, option.value)} className={`h-4 w-4 rounded border-muted-foreground ${textColorClass} ${focusRingColorClass}`} style={{ ...textColorStyle, ...focusRingColorStyle }}/>
                                <label htmlFor={`filter-${section.id}-${optionIdx}`} className="ml-3 text-sm text-foreground" >
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </>
            )}
          </form>
          <div className="lg:col-span-3">
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10">
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
                <SkeletonProduct aspectRatioClass={aspectRatioClass} />
              </div>
            ) : products.length === 0 ? (
              <ProductNotFound />
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-10">
                  {products.map((product: any) => (
                    <ProductCard key={product.id} product={product} vendor={vendor} />
                  ))}
                  {Array.from({ length: pendingCount }).map((_, i) => (
                    <div key={`pending-${i}`} ref={i === 0 ? loadMoreRef : undefined}>
                      <SkeletonProduct aspectRatioClass={aspectRatioClass} animate={false} />
                    </div>
                  ))}
                </div>
                <div ref={pendingCount === 0 ? loadMoreRef : null} className="w-full pt-12 flex justify-center">
                  {isLoadingMore && (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Ачаалж байна...</span>
                    </div>
                  )}
                  {!hasMore && products.length > 0 && (
                    <div className="flex flex-col items-center gap-4">
                      <span className="text-sm text-muted-foreground">Бүх бүтээгдэхүүн ачаалагдлаа ({metadata.total})</span>
                      <Button onClick={scrollToTop} variant="outline" size="sm" icon={ArrowUp}>Дээш буцах</Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  )
}

export function ProductNotFound() {
  return (
    <div className="cont">
      <EmptyState icon={PackageOpen} title="Бүтээгдэхүүн хоосон байна" description="Яг одоогоор ямар нэгэн бүтээгдэхүүн байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу." />
    </div>
  )
}

// animate=false for the pending tail — hundreds of pulsing placeholders is its own jank
export function SkeletonProduct({ aspectRatioClass, animate = true }: { aspectRatioClass?: string, animate?: boolean }) {
  const defaultAspectRatio = aspectRatioClass || 'aspect-w-1 aspect-h-1'
  const still = animate ? '' : 'animate-none'
  return (
    <span className="group text-sm cursor-pointer">
      <div className={`${defaultAspectRatio} w-full relative overflow-hidden rounded-lg`}>
        <Skeleton className={`absolute inset-0 w-full h-full ${still}`} />
      </div>
      <div className="grid xl:hidden w-full grid-cols-2 gap-2 mt-2 h-7">
        <Skeleton className={`w-full h-full rounded ${still}`} />
        <Skeleton className={`w-full h-full rounded ${still}`} />
      </div>
      <div className="flex flex-col text-center mt-2 space-y-2">
        <Skeleton className={`h-5 w-full rounded ${still}`} />
        <Skeleton className={`h-4 w-3/4 mx-auto rounded ${still}`} />
        <Skeleton className={`h-4 w-1/2 mx-auto rounded ${still}`} />
      </div>
    </span>
  )
}